import api from 'services/api';
import { getCompanyContext } from 'utils/setCompanyContext';
import { retryRequest } from 'utils/retryRequest';

class ProdutosUsadosOSService {
  /**
   * Obter empresa correta baseada no contexto
   */
  getCompanyName(params = {}) {
    // Se empresa foi fornecida explicitamente, usar ela
    if (params.empresa) {
      return params.empresa;
    }

    // Obter contexto da empresa
    const companyContext = getCompanyContext();
    
    if (companyContext?.companies?.length > 0) {
      if (companyContext.isConsolidated || companyContext.isGroup) {
        // Para consolidação, retornar null para indicar consolidação
        return null;
      } else {
        // Para empresa individual, usar o nome da empresa
        return companyContext.companies[0].name;
      }
    }
    
    // Fallback para MOV Fibra
    return 'MOV Fibra';
  }

  /**
   * Buscar produtos usados em OS
   * Retorna todos os dados compilados do endpoint
   * Por padrão, usa o primeiro dia do mês até hoje
   * Com retry automático para timeouts
   * @param {Object} params - Parâmetros da requisição
   * @param {AbortSignal} signal - Signal para cancelar a requisição
   */
  async buscarProdutosUsadosOS(params = {}, signal = null) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }

      // Adicionar filtros de cidades se fornecidos
      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }

      if (params.assuntos && Array.isArray(params.assuntos) && params.assuntos.length > 0) {
        requestData.assuntos = params.assuntos;
      }

      if (params.tecnicos && Array.isArray(params.tecnicos) && params.tecnicos.length > 0) {
        requestData.tecnicos = params.tecnicos;
      }

      const config = signal ? { signal } : {};
      
      // Usar retry para requisições com timeout
      const response = await retryRequest(
        () => api.post('/n8n/estoque/produtos_usados_os', requestData, config),
        {
          maxRetries: 3,
          baseDelay: 1000, // 1 segundo
          signal: signal, // Passar signal para respeitar cancelamento
          onRetry: (_attempt, maxRetries, delay, error) => {
            console.warn(`⏳ Tentativa ${_attempt}/${maxRetries} após ${delay}ms`, {
              error: error.message,
              code: error.code
            });
          },
          shouldRetry: (error, _attempt) => {
            // Não fazer retry se foi cancelado pelo AbortController
            if (error.name === 'AbortError' || error.name === 'CanceledError') {
              return false;
            }
            return true;
          }
        }
      );

      // Retornar todos os dados compilados
      // Verificar se é resposta de processamento (status 202)
      if (response.status === 202 || response.data?.status === 'processing') {
        const processingData = response.data?.data && typeof response.data.data === 'object'
          ? response.data.data
          : {};

        const garantirArray = (valor) => {
          if (Array.isArray(valor)) return valor;
          if (valor == null || valor === undefined || valor === false) return [];
          return [];
        };

        return {
          success: false,
          data: {
            produtos_usados_por_oss: garantirArray(processingData.produtos_usados_por_oss),
            ranking_top10_produtos_por_cidade: garantirArray(processingData.ranking_top10_produtos_por_cidade),
            detalhamento_oss_produtos_utilizados: garantirArray(processingData.detalhamento_oss_produtos_utilizados),
            top10_qtd_produtos_por_cidade: garantirArray(processingData.top10_qtd_produtos_por_cidade),
            analise_produtos_movimentados_oss: garantirArray(processingData.analise_produtos_movimentados_oss),
            evolucao_custo_12m: garantirArray(processingData.evolucao_custo_12m),
            assuntos_disponiveis: garantirArray(processingData.assuntos_disponiveis)
          },
          status: 'processing',
          progress: response.data?.progress || null,
          actions: response.data?.actions || [],
          snapshot_used: response.data?.snapshot_used === true,
          snapshot_updated_at: response.data?.snapshot_updated_at || null,
          snapshot_age_seconds: response.data?.snapshot_age_seconds ?? null
        };
      }
      
      // Estrutura normal (SWR ou consolidada)
      // Para SWR: response.data.data contém os dados
      // Para intervalos: response.data contém os dados diretamente
      let dadosCompletos = {};
      
      if (response.data?.data?.data) {
        // Estrutura SWR: { data: { data: { produtos_usados_por_oss: [...] } } }
        dadosCompletos = response.data.data.data;
      } else if (response.data?.data) {
        // Estrutura intervalos: { data: { produtos_usados_por_oss: [...] } }
        dadosCompletos = response.data.data;
      } else if (response.data) {
        // Fallback: { data: { produtos_usados_por_oss: [...] } }
        dadosCompletos = response.data;
      }
      
      // Função helper para garantir que seja array
      const garantirArray = (valor) => {
        if (Array.isArray(valor)) return valor;
        if (valor == null || valor === undefined || valor === false) return [];
        console.warn('Valor não é array, convertendo:', { tipo: typeof valor, valor });
        return [];
      };
      
      // Normalizar dados completos para garantir que todos os arrays sejam realmente arrays
      if (dadosCompletos) {
        dadosCompletos.produtos_usados_por_oss = garantirArray(dadosCompletos.produtos_usados_por_oss);
        dadosCompletos.ranking_top10_produtos_por_cidade = garantirArray(dadosCompletos.ranking_top10_produtos_por_cidade);
        dadosCompletos.detalhamento_oss_produtos_utilizados = garantirArray(dadosCompletos.detalhamento_oss_produtos_utilizados);
        dadosCompletos.top10_qtd_produtos_por_cidade = garantirArray(dadosCompletos.top10_qtd_produtos_por_cidade);
        dadosCompletos.analise_produtos_movimentados_oss = garantirArray(dadosCompletos.analise_produtos_movimentados_oss);
        dadosCompletos.assuntos_disponiveis = garantirArray(dadosCompletos.assuntos_disponiveis);
      }
      
      // Retornar com success: true para indicar que os dados estão prontos
      return {
        success: true,
        data: dadosCompletos,
        cached: response.data?.cached ?? false,
        stale: response.data?.stale ?? false
      };
    } catch (error) {
      // Ignorar erros de cancelamento
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }
      
      console.error('❌ Erro ao buscar produtos usados em OS:', error);
      
      // Retornar estrutura vazia em caso de erro após todas as tentativas
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Erro ao buscar dados',
        data: {
          produtos_usados_por_oss: [],
          ranking_top10_produtos_por_cidade: [],
          detalhamento_oss_produtos_utilizados: [],
          top10_qtd_produtos_por_cidade: [],
          analise_produtos_movimentados_oss: [],
          assuntos_disponiveis: []
        }
      };
    }
  }

  /**
   * Buscar evolução de custo mensal e custo médio (12 meses)
   * Endpoint externo: (prefix)-operacional/estoque/produtos_usados_os
   * Action: evolucao_custo_12m
   */
  async buscarEvolucaoCusto12M(params = {}, signal = null) {
    try {
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      const empresa = this.getCompanyName(params);

      const requestData = {
        action: 'evolucao_custo_12m',
        data_inicio: dataInicio,
        data_fim: dataFim
      };

      if (empresa) {
        requestData.empresa = empresa;
      }

      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }

      if (params.assuntos && Array.isArray(params.assuntos) && params.assuntos.length > 0) {
        requestData.assuntos = params.assuntos;
      }

      if (params.tecnicos && Array.isArray(params.tecnicos) && params.tecnicos.length > 0) {
        requestData.tecnicos = params.tecnicos;
      }

      const config = signal ? { signal } : {};
      const response = await api.post('/n8n/estoque/produtos_usados_os', requestData, config);
      const payload = response?.data;

      let lista = [];
      if (Array.isArray(payload?.evolucao_custo_12m)) {
        lista = payload.evolucao_custo_12m;
      } else if (Array.isArray(payload?.data?.evolucao_custo_12m)) {
        lista = payload.data.evolucao_custo_12m;
      } else if (Array.isArray(payload)) {
        if (Array.isArray(payload[0]?.evolucao_custo_12m)) {
          lista = payload[0].evolucao_custo_12m;
        } else {
          lista = payload;
        }
      } else if (Array.isArray(payload?.data)) {
        if (Array.isArray(payload.data[0]?.evolucao_custo_12m)) {
          lista = payload.data[0].evolucao_custo_12m;
        }
      }

      const dadosNormalizados = (Array.isArray(lista) ? lista : []).map((item) => ({
        cidade: item?.cidade || '',
        assunto: item?.assunto || item?.ASSUNTO || '',
        mes: item?.mes || '',
        custo: Number(item?.custo ?? item?.custo_total ?? item?.CUSTO ?? item?.CUSTO_TOTAL ?? 0),
        qtd_os: Number(item?.['qtd_o.s.'] ?? item?.qtd_os ?? item?.qtd_o_s ?? 0),
        id_tecnico: item?.id_tecnico ?? item?.ID_TECNICO ?? null,
        tecnico: item?.tecnico || item?.TECNICO || item?.['TÉCNICO'] || ''
      }));

      return {
        success: true,
        data: dadosNormalizados
      };
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }

      console.error('❌ Erro ao buscar evolução de custo 12M:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Erro ao buscar evolução de custos',
        data: []
      };
    }
  }

  /**
   * Buscar produtos almoxarifados filtrados
   */
  async buscarProdutosAlmoxarifadosFiltrados(params = {}) {
    try {
      const empresa = this.getCompanyName(params);
      
      // Calcular primeiro e último dia do mês atual
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      const requestData = {
        data_inicio: params.data_inicio || primeiroDiaMes.toISOString().split('T')[0],
        data_fim: params.data_fim || ultimoDiaMes.toISOString().split('T')[0]
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/produtos-almoxarifados-filtrados', requestData);

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar produtos almoxarifados filtrados:', error);
      throw error;
    }
  }

  /**
   * Buscar equipamentos para testes
   */
  async buscarEquipamentosParaTestes(params = {}) {
    try {
      const empresa = this.getCompanyName(params);
      
      
      const requestData = {
        data_inicio: params.data_inicio || new Date().toISOString().split('T')[0],
        data_fim: params.data_fim || new Date().toISOString().split('T')[0]
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/equipamentos-para-testes', requestData);

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar equipamentos para testes:', error);
      throw error;
    }
  }

  /**
   * Buscar OSS Estoque
   * Com retry automático para timeouts
   */
  async buscarOSSEstoque(params = {}, signal = null) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }

      if (params.assuntos && Array.isArray(params.assuntos) && params.assuntos.length > 0) {
        requestData.assuntos = params.assuntos;
      }

      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }
      
      const config = {};
      if (signal) {
        config.signal = signal;
      }
      
      // Usar retry para requisições com timeout
      const response = await retryRequest(
        () => api.post('/n8n/estoque/oss-estoque', requestData, config),
        {
          maxRetries: 3,
          baseDelay: 1000, // 1 segundo
          onRetry: (_attempt, maxRetries, delay, error) => {
            console.warn(`⏳ Tentativa ${_attempt}/${maxRetries} após ${delay}ms`, {
              error: error.message,
              code: error.code
            });
          },
          shouldRetry: (error, _attempt) => {
            // Não fazer retry se foi cancelado pelo AbortController
            if (error.name === 'AbortError' || error.name === 'CanceledError') {
              return false;
            }
            return true;
          }
        }
      );

      const payload = response?.data || {};

      if (payload?.success === false) {
        const message = payload?.message || payload?.error || 'Falha ao carregar OSs Estoque';
        const err = new Error(message);
        err.payload = payload;
        throw err;
      }

      // Normalizar diferentes envelopes possíveis do SWR/cache
      let dadosCompletos = {};
      if (payload?.data?.data && typeof payload.data.data === 'object') {
        dadosCompletos = payload.data.data;
      } else if (payload?.data && typeof payload.data === 'object') {
        dadosCompletos = payload.data;
      } else if (typeof payload === 'object') {
        dadosCompletos = payload;
      }

      const garantirArray = (valor) => (Array.isArray(valor) ? valor : []);
      const garantirOss = (valor) => (valor && Array.isArray(valor.oss) ? { oss: valor.oss } : { oss: [] });

      const normalizado = {
        kpis: (dadosCompletos?.kpis && typeof dadosCompletos.kpis === 'object')
          ? dadosCompletos.kpis
          : { agregado: { TOTAL: 0, media_por_dia: 0 }, por_assunto: [] },
        por_dia: garantirOss(dadosCompletos?.por_dia),
        por_diagnostico: garantirOss(dadosCompletos?.por_diagnostico),
        por_assunto: garantirOss(dadosCompletos?.por_assunto),
        por_tempo: garantirOss(dadosCompletos?.por_tempo),
        por_tecnico: garantirOss(dadosCompletos?.por_tecnico),
        por_cidade: garantirOss(dadosCompletos?.por_cidade),
        por_bairro: garantirOss(dadosCompletos?.por_bairro),
        por_mes: garantirOss(dadosCompletos?.por_mes),
        detalhamento: garantirOss(dadosCompletos?.detalhamento),
        nao_finalizada: garantirOss(dadosCompletos?.nao_finalizada),
        assuntos_disponiveis: garantirArray(dadosCompletos?.assuntos_disponiveis),
      };

      return {
        success: payload?.success ?? true,
        data: normalizado,
        cached: payload?.cached ?? false,
        stale: payload?.stale ?? false,
      };
    } catch (error) {
      // Ignorar erros de cancelamento
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }
      
      console.error('❌ Erro ao buscar OSS Estoque:', error);
      throw error;
    }
  }

  /**
   * Limpar cache geral
   */
  async limparCache(empresa = null) {
    try {
      // Obter empresa correta se não foi fornecida
      if (!empresa) {
        empresa = this.getCompanyName();
      }
      
      
      const requestData = {};
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/limpar-cache', requestData);

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error);
      throw error;
    }
  }

  /**
   * Limpar cache específico de produtos usados OS
   */
  async limparCacheProdutosUsadosOS(empresa = 'MOV Fibra') {
    try {
      
      // Calcular primeiro dia do mês e hoje para gerar a mesma chave de cache
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = hoje.toISOString().split('T')[0];
      
      const response = await api.post('/n8n/estoque/limpar-cache', {
        empresa,
        data_inicio: dataInicio,
        data_fim: dataFim,
        cache_key: 'produtos_usados_os_compilado'
      });

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao limpar cache de produtos usados OS:', error);
      throw error;
    }
  }

  /**
   * Buscar Entradas NF/Produtos
   * Por padrão, usa o primeiro dia do mês até hoje
   */
  async buscarEntradasNFProdutos(params = {}) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      
      
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }

      if (params.subgrupos && Array.isArray(params.subgrupos) && params.subgrupos.length > 0) {
        requestData.subgrupos = params.subgrupos;
      }

      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }
      
      const response = await api.post('/n8n/estoque/entradas-nf-produtos', requestData);
      const payload = response?.data || {};

      let dadosCompletos = {};
      if (payload?.data?.data && typeof payload.data.data === 'object') {
        dadosCompletos = payload.data.data;
      } else if (payload?.data && typeof payload.data === 'object') {
        dadosCompletos = payload.data;
      } else if (typeof payload === 'object') {
        dadosCompletos = payload;
      }

      return {
        success: payload?.success ?? true,
        data: dadosCompletos,
        cached: payload?.cached ?? false,
        stale: payload?.stale ?? false
      };
    } catch (error) {
      console.error('❌ Erro ao buscar Entradas NF/Produtos:', error);
      throw error;
    }
  }

  /**
   * Buscar Entradas NF/Serviços
   * Por padrão, usa o primeiro dia do mês até hoje
   */
  async buscarEntradasNFServicos(params = {}) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      
      
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }

      if (params.subgrupos && Array.isArray(params.subgrupos) && params.subgrupos.length > 0) {
        requestData.subgrupos = params.subgrupos;
      }

      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }
      
      const response = await api.post('/n8n/estoque/entradas-nf-servicos', requestData);
      const payload = response?.data || {};

      let dadosCompletos = {};
      if (payload?.data?.data && typeof payload.data.data === 'object') {
        dadosCompletos = payload.data.data;
      } else if (payload?.data && typeof payload.data === 'object') {
        dadosCompletos = payload.data;
      } else if (typeof payload === 'object') {
        dadosCompletos = payload;
      }

      return {
        success: payload?.success ?? true,
        data: dadosCompletos,
        cached: payload?.cached ?? false,
        stale: payload?.stale ?? false
      };
    } catch (error) {
      console.error('❌ Erro ao buscar Entradas NF/Serviços:', error);
      throw error;
    }
  }

  /**
   * Buscar Movimentação Interna/Entrada
   * Por padrão, usa o primeiro dia do mês até hoje
   */
  async buscarMovimentacaoInternaEntrada(params = {}) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      
      
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/movimentacao-interna-entrada', requestData);

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar Movimentação Interna/Entrada:', error);
      throw error;
    }
  }

  /**
   * Buscar Movimentação Interna/Saída
   * Por padrão, usa o primeiro dia do mês até hoje
   */
  async buscarMovimentacaoInternaSaida(params = {}) {
    try {
      // Calcular primeiro dia do mês e hoje
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      
      const dataInicio = params.data_inicio || primeiroDiaMes.toISOString().split('T')[0];
      const dataFim = params.data_fim || hoje.toISOString().split('T')[0];
      
      
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/movimentacao-interna-saida', requestData);

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar Movimentação Interna/Saída:', error);
      throw error;
    }
  }

  /**
   * Verificar status de processamento de intervalos
   * 
   * @param {Object} params - Parâmetros: {data_inicio, data_fim, empresa, action}
   * @param {AbortSignal} signal - Signal para cancelar a requisição
   * @returns {Promise<Object>} Status dos intervalos: {status, progress, intervals}
   */
  async verificarStatus(params = {}, signal = null) {
    try {
      const empresa = this.getCompanyName(params);
      
      if (!params.data_inicio || !params.data_fim) {
        throw new Error('data_inicio e data_fim são obrigatórios');
      }

      const requestData = {
        data_inicio: params.data_inicio,
        data_fim: params.data_fim,
        action: 'base'
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const config = signal ? { signal } : {};
      const response = await api.post('/n8n/estoque/produtos-usados-os/status', requestData, config);

      return response.data;
    } catch (error) {
      // Re-throw erros de cancelamento para que o componente possa tratá-los
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }
      console.error('❌ Erro ao verificar status:', error);
      throw error;
    }
  }

  /**
   * Limpar cache genérico para qualquer endpoint de estoque
   * 
   * @param {string} endpoint - Nome do endpoint (ex: 'oss-estoque', 'produtos-usados-os')
   * @param {Object} params - Parâmetros: {data_inicio?, data_fim?, empresa?}
   * @returns {Promise<Object>} Resultado da limpeza
   */
  async limparCacheGenerico(endpoint, params = {}) {
    try {
      const empresa = this.getCompanyName(params);
      
      const requestData = {
        endpoint: endpoint
      };
      
      // Adicionar parâmetros se fornecidos
      if (params.data_inicio) {
        requestData.data_inicio = params.data_inicio;
      }
      if (params.data_fim) {
        requestData.data_fim = params.data_fim;
      }
      if (empresa) {
        requestData.empresa = empresa;
      }
      
      const response = await api.post('/n8n/estoque/limpar-cache-generico', requestData);
      
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao limpar cache genérico:', error);
      throw error;
    }
  }

  /**
   * Buscar dados KPI (derivado da resposta canônica)
   * 
   * @param {Object} params - Parâmetros: {data_inicio, data_fim, empresa, assuntos?}
   * @returns {Promise<Object>} Dados KPI: {total_os, custo_total}
   */
  async buscarKPI(params = {}) {
    try {
      const empresa = this.getCompanyName(params);
      
      if (!params.data_inicio || !params.data_fim) {
        throw new Error('data_inicio e data_fim são obrigatórios');
      }
      
      const requestData = {
        data_inicio: params.data_inicio,
        data_fim: params.data_fim
      };
      
      // Só adicionar empresa se não for consolidação
      if (empresa) {
        requestData.empresa = empresa;
      }

      // Adicionar filtros de assuntos se fornecidos
      if (params.assuntos && Array.isArray(params.assuntos) && params.assuntos.length > 0) {
        requestData.assuntos = params.assuntos;
      }

      // Adicionar filtros de cidades se fornecidos
      if (params.cidades && Array.isArray(params.cidades) && params.cidades.length > 0) {
        requestData.cidades = params.cidades;
      }

      // Adicionar filtros de técnicos se fornecidos
      if (params.tecnicos && Array.isArray(params.tecnicos) && params.tecnicos.length > 0) {
        requestData.tecnicos = params.tecnicos;
      }
      
      const response = await api.post('/n8n/estoque/produtos_usados_os', requestData);
      const payload = response?.data?.data || {};
      const produtos = Array.isArray(payload.produtos_usados_por_oss) ? payload.produtos_usados_por_oss : [];
      const totalOS = produtos.length;
      const custoTotal = produtos.reduce((sum, item) => sum + Number(item?.custo || 0), 0);

      return {
        success: true,
        data: {
          total_os: totalOS,
          custo_total: Number(custoTotal.toFixed(2))
        }
      };
    } catch (error) {
      console.error('❌ Erro ao buscar KPI:', error);
      throw error;
    }
  }

}

// Instância singleton
const produtosUsadosOSService = new ProdutosUsadosOSService();

export default produtosUsadosOSService;
